import type { ReactNode } from 'react';
import {
  UMCheckbox,
  UMCheckboxRow,
  UMFormGroup,
  UMIcon,
  UMInput,
  UMMultiSelect,
  UMR,
  UMRadioGroup,
  UMRangeSlider,
  UMSegmentedControl,
  UMSelect,
  type UMRange,
} from '../_components';
import { normalize } from './matching';
import type { DateValue, DeclaredValue, FieldDef, FilterValue } from './types';

/** Official DPE scale colours (dataset colours, like map paint — hex allowed). */
const DPE_COLORS: Record<string, { bg: string; fg: string }> = {
  A: { bg: '#009036', fg: '#ffffff' },
  B: { bg: '#55aa31', fg: '#ffffff' },
  C: { bg: '#c8d200', fg: '#000000' },
  D: { bg: '#ffed00', fg: '#000000' },
  E: { bg: '#fbba00', fg: '#000000' },
  F: { bg: '#eb6909', fg: '#ffffff' },
  G: { bg: '#e2001a', fg: '#ffffff' },
};
const YES_NO = ['Indifférent', 'Oui', 'Non'];

export function highlight(text: string, term: string): ReactNode {
  const q = normalize(term.trim());
  if (!q) return text;
  const i = normalize(text).indexOf(q);
  if (i < 0) return text;
  return (
    <>
      {text.slice(0, i)}
      <mark style={{ background: UMR.bluePressed, color: 'inherit', padding: 0 }}>{text.slice(i, i + q.length)}</mark>
      {text.slice(i + q.length)}
    </>
  );
}

function MinMax({ label, value, onChange }: { label: string; value: UMRange; onChange: (v: UMRange) => void }) {
  return (
    <div style={{ display: 'flex', gap: 16 }}>
      <UMInput type="number" min={0} placeholder="Min" ariaLabel={`${label} min`} value={value.min} onChange={(min) => onChange({ ...value, min })} />
      <UMInput type="number" placeholder="Max" ariaLabel={`${label} max`} value={value.max} onChange={(max) => onChange({ ...value, max })} />
    </div>
  );
}

function DpeScale({ label, value, onChange }: { label: string; value: string[]; onChange: (v: string[]) => void }) {
  return (
    <div role="group" aria-label={label} style={{ display: 'flex', gap: 4 }}>
      {Object.entries(DPE_COLORS).map(([letter, c]) => {
        const on = value.includes(letter);
        return (
          <button
            key={letter}
            type="button"
            aria-pressed={on}
            aria-label={`Classe ${letter}${on ? ', cochée' : ''}`}
            onClick={() => onChange(on ? value.filter((v) => v !== letter) : [...value, letter].sort())}
            style={{
              flex: 1, height: 32, border: on ? `2px solid ${UMR.black}` : `1px solid ${UMR.border}`, cursor: 'pointer',
              background: on ? c.bg : UMR.white, color: on ? c.fg : UMR.textLabel, fontFamily: UMR.font, fontWeight: 700, fontSize: 14,
              boxShadow: on ? 'none' : `inset 0 -4px 0 ${c.bg}`,
            }}
          >
            {letter}
          </button>
        );
      })}
    </div>
  );
}

/** UMInput only types text / number: same measured style, native date picker. */
function DateInput({ label, value, onChange }: { label: string; value: string; onChange: (v: string) => void }) {
  return (
    <input
      className="um-input"
      type="date"
      aria-label={label}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      style={{ width: '100%', minWidth: 0, height: 32, boxSizing: 'border-box', border: `1px solid ${UMR.borderInput}`, borderRadius: 0, padding: '1px 6px', fontSize: 12, fontFamily: 'system-ui', background: UMR.white }}
    />
  );
}

function DateRange({ field, value, onChange }: { field: FieldDef; value: DateValue; onChange: (v: DateValue) => void }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      <div role="radiogroup" aria-label={field.label} style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
        {field.presets!.map((p) => {
          const on = value.preset === p;
          return (
            <button
              key={p}
              type="button"
              role="radio"
              aria-checked={on}
              onClick={() => onChange({ ...value, preset: on ? '' : p })}
              style={{
                borderRadius: 9999, padding: '4px 10px', fontFamily: UMR.font, fontSize: 12, lineHeight: '16px', cursor: 'pointer',
                border: `1px solid ${on ? UMR.blue : UMR.border}`, background: on ? UMR.bluePressed : UMR.white, color: on ? UMR.blueLink : UMR.textLabel,
                fontWeight: on ? 700 : 400,
              }}
            >
              {p}
            </button>
          );
        })}
      </div>
      {value.preset === 'Personnalisée' && (
        <div style={{ display: 'flex', gap: 8 }}>
          <DateInput label={`${field.label} — début`} value={value.from} onChange={(from) => onChange({ ...value, from })} />
          <DateInput label={`${field.label} — fin`} value={value.to} onChange={(to) => onChange({ ...value, to })} />
        </div>
      )}
    </div>
  );
}

export interface FieldControlProps {
  field: FieldDef;
  label: string;
  value: FilterValue;
  onChange: (v: FilterValue) => void;
  search: string;
  /** Value shown instead of the stored one (CRM « Présent dans une offre » implied by other criteria). */
  displayValue?: string;
}

export function FieldControl({ field: f, label, value, onChange, search, displayValue }: FieldControlProps) {
  const title = (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
      {highlight(label, search)}
      {f.isNew && <UMIcon name="fiber_new" size={16} color={UMR.blue} style={{ marginLeft: 2 }} />}
    </span>
  );

  switch (f.kind) {
    case 'perimeter':
      return <UMInput value={(value as string) ?? ''} onChange={onChange} placeholder={f.placeholder} icon="search" />;
    case 'segmented':
      return <div><UMSegmentedControl options={f.options!} value={(value as string) ?? f.options![0]} onChange={onChange} /></div>;
    case 'toggle':
      return <UMCheckbox checked={value === true} onChange={onChange} label={title} />;
    case 'multiselect':
      return (
        <UMFormGroup label={title}>
          <UMMultiSelect ariaLabel={label} options={f.options!} value={(value as string[]) ?? []} onChange={onChange} />
        </UMFormGroup>
      );
    case 'checkboxes':
      return (
        <UMFormGroup label={f.id === 'networkType' ? undefined : title}>
          <UMCheckboxRow options={f.options!} value={(value as string[]) ?? []} onChange={onChange} />
        </UMFormGroup>
      );
    case 'range':
      return (
        <UMFormGroup label={title}>
          <MinMax label={label} value={(value as UMRange) ?? { min: '', max: '' }} onChange={onChange} />
        </UMFormGroup>
      );
    case 'radio':
      return (
        <UMFormGroup label={title}>
          <UMRadioGroup name={f.id} options={f.options!} value={displayValue ?? (value as string) ?? f.options![0]} onChange={onChange} />
        </UMFormGroup>
      );
    case 'text':
    case 'exact':
      return (
        <UMFormGroup label={title}>
          <UMInput ariaLabel={label} placeholder={f.placeholder} value={(value as string) ?? ''} onChange={onChange} />
        </UMFormGroup>
      );
    case 'select':
      return (
        <UMFormGroup label={title}>
          <UMSelect ariaLabel={label} value={(value as string) ?? f.options![0]} options={f.options!.map((o) => ({ value: o, label: o }))} onChange={onChange} />
        </UMFormGroup>
      );
    case 'distance':
      return (
        <UMFormGroup label={title}>
          <UMRangeSlider
            min={0}
            max={f.max!}
            step={f.step!}
            value={(value as [number, number]) ?? [0, f.max!]}
            onChange={onChange}
            format={(v) => (v >= f.max! - f.step! ? `${f.max! - f.step!}+ m` : `${v} m`)}
          />
        </UMFormGroup>
      );
    case 'dpe':
      return (
        <UMFormGroup label={title}>
          <DpeScale label={label} value={(value as string[]) ?? []} onChange={onChange} />
        </UMFormGroup>
      );
    case 'dateRange':
      return (
        <UMFormGroup label={title}>
          <DateRange field={f} value={(value as DateValue) ?? { preset: '', from: '', to: '' }} onChange={onChange} />
        </UMFormGroup>
      );
    case 'declared': {
      const d = (value as DeclaredValue) ?? { state: 'Indifférent', min: '', max: '' };
      return (
        <UMFormGroup label={title}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <div style={{ fontSize: 12, color: UMR.textSecondary }}>Donnée renseignée</div>
            <UMRadioGroup name={f.id} options={YES_NO} value={d.state} onChange={(state) => onChange({ ...d, state, ...(state === 'Non' ? { min: '', max: '' } : {}) })} />
            {d.state !== 'Non' && <MinMax label={label} value={d} onChange={(r) => onChange({ ...d, ...r })} />}
          </div>
        </UMFormGroup>
      );
    }
  }
}
